import { useCallback, useEffect, useRef, useState } from "react";
import {
	dataGridPersistentStateProps,
	DEFAULT_GRID_INITIAL_STATE
} from "src/components/common/dataGrid/dataGridStateStorage.ts";
import { useForwardedRef } from "src/utils/useForwardedRef.ts";
import { AsyncState, useAsyncState } from "src/utils/async/asyncState.ts";
import { logError, LoggedError } from "src/errorHandling/errorLogging.ts";
import { useMaybeControlledState } from "src/utils/useMaybeControlledState.ts";
import {
	UseDataGridModelCommonParams,
	UseDataGridModelCommonReturn,
	UseDataGridModelGridCommonProps,
	usePersistentDataGridId,
} from "src/components/common/dataGrid/gridModel/dataGridModelCommon.ts";
import { setRefreshRefValue } from "src/utils/useRefreshRef";
import { GRID_DETAIL_PANEL_TOGGLE_FIELD, GridInitialState, GridRowId } from "@mui/x-data-grid-pro";
import { DataGridCsvExportButton } from "src/components/common/dataGrid/csvDownload/DataGridCsvExportButton.tsx";
import { usePartiallyStoredState } from "src/utils/usePartiallyStoredState.ts";
import { setColumnVisibility } from "src/components/common/dataGrid/utils.ts";
import {
	applyDataGridRowUpdates,
	DataGridRowUpdateHandler,
} from "src/components/common/dataGrid/gridModel/dataGridRowUpdates.ts";

export interface UseClientSideDataGridModelParams<RowData extends object, TParams extends object>
	extends UseDataGridModelCommonParams<RowData, TParams> {
	fetchData: (params: TParams) => Promise<RowData[]>;
	hideDetailPanelToggle?: (rows: RowData[]) => boolean;
	mapGridState?: (gridState: GridInitialState) => GridInitialState;
}

export interface UseClientSideDataGridModelReturn<RowData extends object, TParams>
	extends UseDataGridModelCommonReturn<RowData, TParams> {
	dataGridProps: UseClientSideDataGridModelGridProps<RowData>;
}

export interface UseClientSideDataGridModelGridProps<RowData extends object>
	extends UseDataGridModelGridCommonProps<RowData> {
	updateRows: DataGridRowUpdateHandler;
}

export const useClientSideDataGridModel = <RowData extends object, TParams extends object>({
	gridId,
	getRowId,
	fetchData,
	initialParams,
	storedParams = [],
	apiRef: apiRefProp,
	selectedRows: selectedRowsProp,
	defaultSelectedRows = [],
	onSelectionChanged: setSelectedRowsProp,
	selectFirstRowOnLoad,
	defaultGridState = DEFAULT_GRID_INITIAL_STATE,
	refreshRef,
	hideDetailPanelToggle: hideDetailPanelToggleFunc,
	mapGridState,
	customFunctionUiActionView,
	customFunctionUiActionObjectId,
}: UseClientSideDataGridModelParams<RowData, TParams>): UseClientSideDataGridModelReturn<RowData, TParams> => {
	const apiRef = useForwardedRef(apiRefProp);
	const persistentGridId = usePersistentDataGridId(gridId);
	const [currentParams, setCurrentParams] = usePartiallyStoredState<TParams>({
		initialState: initialParams,
		storedKeys: storedParams,
		key: "data-grid-params--" + gridId,
	});
	const [rowsAsync, setRowsAsync, setRowsImmediate] = useAsyncState<RowData[]>({});
	const [error, setError] = useState<LoggedError | undefined>(undefined);
	const rows = rowsAsync.data ?? [];
	const rowsRef = useRef<RowData[]>(rows);
	rowsRef.current = rows;

	const { initialState: initialGridState, onStateChange: onGridStateChange } = dataGridPersistentStateProps(
		persistentGridId,
		defaultGridState,
	);

	const [selectedRows, setSelectedRows] = useMaybeControlledState({
		controlledValue: selectedRowsProp,
		onChange: setSelectedRowsProp,
		defaultValue: defaultSelectedRows,
	});
	const onlySelectedRow = selectedRows.length === 1 ? selectedRows[0] : undefined;

	const onRowSelectionChanged = (selectedRowIds: GridRowId[]) => {
		const selectedRows = rowsRef.current.filter((row) => selectedRowIds.includes(getRowId(row)));
		setSelectedRows(selectedRows);
	};

	const updateRows: DataGridRowUpdateHandler = useCallback((updates) => {
		setRowsImmediate((prev) => {
			const updatedRows = applyDataGridRowUpdates(prev, updates, getRowId);
			rowsRef.current = updatedRows;
			return updatedRows;
		});
	}, [getRowId, setRowsImmediate]);

	const refreshData = async (newParams: Partial<TParams> = {}) => {
		try {
			setCurrentParams((prev) => {
				return {
					...prev,
					...newParams,
				};
			});
			const newRows = await setRowsAsync(() =>
				fetchData({
					...currentParams,
					...newParams,
				}),
			);
			rowsRef.current = newRows;

			if (selectFirstRowOnLoad && selectedRows.length === 0) {
				setSelectedRows(newRows.slice(0, 1));
			} else {
				const newSelectedRows = newRows.filter((row) =>
					selectedRows.some((selectedRow) => getRowId(selectedRow) === getRowId(row)),
				);
				setSelectedRows(newSelectedRows);
			}

			return newRows;
		} catch (e) {
			const loggedError = logError(e);
			setError(loggedError);
			return [];
		}
	};

	if (refreshRef) setRefreshRefValue(refreshRef, (params) => refreshData(params));

	const getApi = () => {
		if (!apiRef.current) {
			throw new Error("DataGrid API ref is not set");
		}
		return apiRef.current;
	};

	useEffect(() => {
		refreshData().catch(logError);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		dataGridProps: {
			customFunctionUiActionView,
			customFunctionUiActionObjectId,
			apiRef: apiRef,
			initialState: resolveGridState(),
			onStateChange: onGridStateChange,
			refreshData: () => refreshData(),
			updateRows: updateRows,
			selectedRows: selectedRows.map(getRowId),
			onRowSelectionChanged: onRowSelectionChanged,
			getRowId: getRowId,
			gridId: persistentGridId,
			actionBarMenuComponents2: [
				<DataGridCsvExportButton
					key={"dataGridDefaultExportDataButton"}
					dataModel={{
						type: "clientSide",
						rows: rows,
					}}
					getApi={getApi}
				/>,
			],
			rowsAsync: {
				data: rows,
				loading: rowsAsync.loading,
				error: error,
			},
		},
		currentParams: currentParams,
		refreshData: refreshData,
		selectedRows: selectedRows,
		setSelectedRows: setSelectedRows,
		onlySelectedRow: onlySelectedRow,
		getApi: getApi,
	};

	function resolveGridState() {
		const detailPanelToggleMaybeHidden = maybeHideDetailPanelToggle(
			initialGridState,
			rowsAsync,
			hideDetailPanelToggleFunc,
		);
		return mapGridState != null ? mapGridState(detailPanelToggleMaybeHidden) : detailPanelToggleMaybeHidden;
	}
};

function maybeHideDetailPanelToggle<RowData>(
	gridState: GridInitialState,
	rowsAsync: AsyncState<RowData[]>,
	hideDetailPanelToggleFunc: ((rows: RowData[]) => boolean) | undefined,
): GridInitialState {
	if (!hideDetailPanelToggleFunc || rowsAsync.data === undefined) {
		return gridState;
	}
	const hideDetailPanelToggle = hideDetailPanelToggleFunc(rowsAsync.data);
	if (!hideDetailPanelToggle) return gridState;

	return setColumnVisibility(gridState, GRID_DETAIL_PANEL_TOGGLE_FIELD, false);
}
